import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import "../Style/ViewProduct.css";
import NavBar from "../Components/NavBar";
import Footer from "../Components/Footer";
import { api } from "../api/client.js";
import { resolveProductMediaUrl } from "../data/products";
import CandleRequestModal from "../Components/CandleRequestModal.js";
import BuildOrderModal from "../Components/BuildOrderModal.js";
import PurchaseModal from "../Components/PurchaseModal.js";
import TermsAndConditions from "../Components/TermsAndConditions.js";

const PRICE_NOTE = "Final price Tax included. +shipping fee";

function ViewProduct() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeMediaIndex, setActiveMediaIndex] = useState(0);
  const [apiWarning, setApiWarning] = useState("");
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [purchaseItem, setPurchaseItem] = useState(null);
  const [purchaseItemType, setPurchaseItemType] = useState("product");
  const [purchaseCandleIds, setPurchaseCandleIds] = useState([]);
  const [termsOpen, setTermsOpen] = useState(false);
  const [termsRequiredOpen, setTermsRequiredOpen] = useState(false);
  const [buildOrderOpen, setBuildOrderOpen] = useState(false);
  const [buildTermsOpen, setBuildTermsOpen] = useState(false);
  const [buildTermsRequiredOpen, setBuildTermsRequiredOpen] = useState(false);
  const [refillOpen, setRefillOpen] = useState(false);
  const mainMedia = product
    ? {
        id: `${product.id}-main`,
        type: "image",
        src: resolveProductMediaUrl(product.imageUrl || product.image),
        alt: product.alt,
      }
    : null;
  const additionalMedia = (product?.media || [])
    .map((media) => ({
      ...media,
      src: resolveProductMediaUrl(media.url || media.src),
      poster:
        resolveProductMediaUrl(media.posterUrl || media.poster) ||
        mainMedia?.src,
    }))
    .filter((media) => media.type === "video" || media.src !== mainMedia?.src);
  const productMedia = mainMedia
    ? [mainMedia, ...additionalMedia]
    : additionalMedia;
  const activeMedia = productMedia[activeMediaIndex] || productMedia[0];

  useEffect(() => {
    setActiveMediaIndex(0);
    setLoading(true);

    const controller = new AbortController();

    api.products
      .get(productId, { signal: controller.signal })
      .then((response) => {
        setProduct(response.data);
        setApiWarning("");
      })
      .catch((error) => {
        if (error.name === "AbortError") return;
        if (error.status !== 404) {
          console.error("Could not load product details from the API", error);
          if (process.env.NODE_ENV === "development") {
            setApiWarning(
              "Development notice: product details could not be loaded from the API.",
            );
          }
        }
        setProduct(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [productId]);

  if (loading) {
    return (
      <div className="product-page">
        <NavBar />
        <main className="product-not-found">
          <p>Loading product…</p>
        </main>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-page">
        <NavBar />
        <main className="product-not-found">
          {apiWarning && (
            <p className="product-api-warning" role="status">
              {apiWarning}
            </p>
          )}
          <p className="product-eyebrow">Up &amp; Away </p>
          <h1>Product not found</h1>
          <p>The piece you are looking for is no longer available here.</p>
          <Link to="/contact-us" className="product-back-link">
            Return home
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const formattedPrice = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: product.currency,
    maximumFractionDigits: 0,
  }).format(product.price);
  const isOutOfStock = product.inStock === false;
  const isBalloonProduct = (product.productType || "balloon") === "balloon";

  const beginPurchase = () => {
    if (isOutOfStock) return;
    setPurchaseItem(product);
    setPurchaseItemType("product");
    setPurchaseCandleIds([]);
    setTermsOpen(true);
  };

  const beginRefillOrder = (selectedCandles) => {
    if (selectedCandles.length === 0) {
      return "Please select at least one refill candle.";
    }

    const candleNames = selectedCandles.map((candle) => candle.fragranceNote);
    setPurchaseItem({
      id:
        selectedCandles.length === 1
          ? selectedCandles[0].id
          : selectedCandles.map((candle) => candle.id).join("-"),
      name:
        selectedCandles.length === 1
          ? selectedCandles[0].fragranceNote
          : `Selected refills (${selectedCandles.length})`,
      mood:
        selectedCandles.length === 1
          ? selectedCandles[0].mood
          : candleNames.join(", "),
      color: selectedCandles[0].color,
      candles: selectedCandles,
      currency: "USD",
    });
    setPurchaseItemType("candle");
    setPurchaseCandleIds(selectedCandles.map((candle) => candle.id));
    setRefillOpen(false);
    setTermsOpen(true);
    return "";
  };

  const acceptTerms = () => {
    setTermsOpen(false);
    setPurchaseOpen(true);
  };

  const declineTerms = () => {
    setTermsOpen(false);
    setPurchaseOpen(false);
    setTermsRequiredOpen(true);
  };

  const acceptBuildTerms = () => {
    setBuildTermsOpen(false);
    setBuildOrderOpen(true);
  };

  const declineBuildTerms = () => {
    setBuildTermsOpen(false);
    setBuildOrderOpen(false);
    setBuildTermsRequiredOpen(true);
  };

  return (
    <div className="product-page">
      <NavBar />

      <main className="product-main">
        {apiWarning && (
          <p className="product-api-warning" role="status">
            {apiWarning}
          </p>
        )}
        <Link to="/contact-us" className="product-back-link">
          <span aria-hidden="true">←</span> Back to collections
        </Link>

        <article className="product-layout">
          <div className="product-gallery">
            <div className="product-image-frame">
              {activeMedia.type === "video" ? (
                <video
                  key={activeMedia.src}
                  src={activeMedia.src}
                  poster={activeMedia.poster}
                  controls
                  playsInline
                  preload="metadata"
                  aria-label={
                    activeMedia.alt || `${product.name} product video`
                  }
                >
                  Your browser does not support the video element.
                </video>
              ) : (
                <img
                  src={activeMedia.src}
                  alt={activeMedia.alt || product.alt}
                />
              )}
            </div>

            <div className="product-media-list" aria-label="Product media">
              {productMedia.map((media, index) => (
                <button
                  type="button"
                  className={`product-media-thumbnail${
                    index === activeMediaIndex ? " active" : ""
                  }`}
                  key={media.id || `${media.type}-${index}`}
                  onClick={() => setActiveMediaIndex(index)}
                  aria-label={`View ${
                    media.type === "video" ? "video" : "image"
                  } ${index + 1} of ${product.name}`}
                  aria-pressed={index === activeMediaIndex}
                >
                  {media.type === "video" ? (
                    <>
                      {media.poster ? (
                        <img src={media.poster} alt="" aria-hidden="true" />
                      ) : (
                        <span className="product-video-placeholder" />
                      )}
                      <span className="product-video-icon" aria-hidden="true">
                        ▶
                      </span>
                    </>
                  ) : (
                    <img src={media.src} alt="" aria-hidden="true" />
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="product-details">
            <p className="product-eyebrow">The Collection</p>
            <h1>{product.name}</h1>
            <p className="product-mood">{product.mood}</p>
            <span className="product-divider" aria-hidden="true" />
            <p className="product-description">{product.description}</p>

            <dl className="product-specs">
              <div className="product-spec">
                <dt>Product name</dt>
                <dd>{product.name}</dd>
              </div>
              <div className="product-spec">
                <dt>Fragrance note</dt>
                <dd>{product.fragranceNote || "Not set"}</dd>
              </div>
              <div className="product-spec">
                <dt>Colors</dt>
                <dd className="product-color">
                  <span
                    className="product-color-swatch"
                    style={{ backgroundColor: product.color.value }}
                    aria-hidden="true"
                  />
                  {product.color.name}

                  {product.secondColor ? (
                    <>
                      <br />
                      <span
                        className="product-color-swatch"
                        style={{ backgroundColor: product.secondColor.value }}
                        aria-hidden="true"
                      />
                      {product.secondColor.name}
                    </>
                  ) : null}
                </dd>
              </div>
              {/* {product.secondColor && (
                <div className="product-spec">
                  <dt>Second color</dt>
                  <dd className="product-color">
                    <span
                      className="product-color-swatch"
                      style={{ backgroundColor: product.secondColor.value }}
                      aria-hidden="true"
                    />
                    {product.secondColor.name}
                  </dd>
                </div>
              )} */}
            </dl>

            <div className="product-purchase">
              <div className="product-price-wrap">
                <p className="product-price">{formattedPrice}</p>
                <p className="product-price-note">{PRICE_NOTE}</p>
              </div>
              {isOutOfStock && (
                <p className="product-stock-status">Out of stock</p>
              )}
              <div className="product-purchase-actions">
                <button
                  type="button"
                  className="product-cta"
                  onClick={beginPurchase}
                  disabled={isOutOfStock}
                >
                  {isOutOfStock ? "Out of stock" : "Purchase this piece"}
                </button>
                <button
                  type="button"
                  className="product-cta"
                  onClick={() => setRefillOpen(true)}
                >
                  Request Refill
                </button>
                <button
                  type="button"
                  className="product-cta"
                  onClick={() => setBuildTermsOpen(true)}
                >
                  Build your order
                </button>
                <Link
                  to={`/reserve-occaission?product=${encodeURIComponent(
                    product.id,
                  )}`}
                  className="product-reserve-link"
                >
                  Reserve an occasion
                </Link>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
      {refillOpen && (
        <CandleRequestModal
          title="Request Refill"
          filters={{ productId: product.id }}
          emptyMessage="No refill candles are linked to this product yet."
          onClose={() => setRefillOpen(false)}
          onOrder={beginRefillOrder}
        />
      )}

      <TermsAndConditions
        isOpen={termsOpen}
        onClose={declineTerms}
        showActions
        acceptText="Accept"
        denyText="Decline"
        onAccept={acceptTerms}
        onDeny={declineTerms}
      />

      <TermsAndConditions
        isOpen={termsRequiredOpen}
        title="Terms & Conditions Required"
        onClose={() => setTermsRequiredOpen(false)}
        closeText="OK"
        showCloseAction
        showHeaderClose={false}
        closeOnBackdrop={false}
      >
        <p>
          We cannot process or sell our products unless you accept the Terms
          &amp; Conditions governing the use of our services.
        </p>
      </TermsAndConditions>

      <TermsAndConditions
        isOpen={buildTermsOpen}
        onClose={declineBuildTerms}
        showActions
        acceptText="Accept"
        denyText="Decline"
        onAccept={acceptBuildTerms}
        onDeny={declineBuildTerms}
      />

      <TermsAndConditions
        isOpen={buildTermsRequiredOpen}
        title="Terms & Conditions Required"
        onClose={() => setBuildTermsRequiredOpen(false)}
        closeText="OK"
        showCloseAction
        showHeaderClose={false}
        closeOnBackdrop={false}
      >
        <p>
          We cannot process or sell our products unless you accept the Terms
          &amp; Conditions governing the use of our services.
        </p>
      </TermsAndConditions>

      {purchaseOpen && purchaseItem && (
        <PurchaseModal
          product={purchaseItem}
          itemType={purchaseItemType}
          candleIds={purchaseCandleIds}
          returnPath={`/products/${encodeURIComponent(product.id)}`}
          onClose={() => setPurchaseOpen(false)}
        />
      )}

      {buildOrderOpen && (
        <BuildOrderModal
          initialProductId={product.id}
          returnPath={`/products/${encodeURIComponent(product.id)}`}
          onClose={() => setBuildOrderOpen(false)}
        />
      )}
    </div>
  );
}

export default ViewProduct;
